import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Database, X, Check, Loader2, Table as TableIcon, Eye, ChevronRight, Layers, ArrowRight } from 'lucide-react';
import { interactiveFetch, apiFetch } from '@/api';

const quoteSqlIdentifier = (name: string) => `"${String(name || '').replace(/"/g, '""')}"`;

export const DataModelSelectionModal = ({ isOpen, onClose, currentModel, onConfirm }: any) => {
    const [models, setModels] = useState<any[]>([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [search, setSearch] = useState('');

    const [selectedModel, setSelectedModel] = useState<any>(currentModel);
    const [previewTable, setPreviewTable] = useState<string | null>(null);

    const [previewData, setPreviewData] = useState<any[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [isPreviewExpanded, setIsPreviewExpanded] = useState(true);

    useEffect(() => {
        if (isOpen) {
            setSelectedModel(currentModel);
            setPreviewTable(null);
            setPreviewData([]);
            setPreviewError(null);
            setIsPreviewExpanded(true);

            setLoadingModels(true);
            apiFetch('/datamodels')
                .then((d: any) => setModels(Array.isArray(d) ? d : (d?.items || d?.data || [])))
                .catch(() => {})
                .finally(() => setLoadingModels(false));
        }
    }, [isOpen, currentModel]);

    const handleModelSelect = (m: any) => {
        setSelectedModel(m);
        setPreviewTable(null);
        setPreviewData([]);
        setPreviewError(null);
        setIsPreviewExpanded(true);
    };

    const handleTablePreview = async (tableName: string) => {
        // Toggle off if already previewing same table
        if (previewTable === tableName) {
            setPreviewTable(null);
            setPreviewData([]);
            setPreviewError(null);
            return;
        }
        setPreviewTable(tableName);
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewData([]);
        try {
            if (!selectedModel?.id) throw new Error('No data model selected');
            const tableSql = quoteSqlIdentifier(tableName);
            const res = await interactiveFetch(`/datamodels/${selectedModel.id}/query`, null, {
                method: 'POST',
                body: JSON.stringify({ sql: `SELECT * FROM ${tableSql} LIMIT 50` })
            });
            setPreviewData(Array.isArray(res?.rows) ? res.rows : []);
        } catch (err: any) {
            setPreviewError(err.message || 'Failed to fetch preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    if (!isOpen) return null;

    const filteredModels = models.filter(m => !search || m.name?.toLowerCase().includes(search.toLowerCase()));
    const tables = selectedModel?.tables || selectedModel?.model_definition?.tables || [];

    const modalContent = (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 sm:p-8"
            onClick={onClose}
            style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
        >
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[1000px] h-[80vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ─────────────────────────────────────────────────────────── */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl">
                            <Database className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-[16px] font-bold text-slate-900 dark:text-slate-100 leading-none">Select Data Model</h2>
                            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1">
                                Choose a data model to power this dashboard.
                                {selectedModel && <span className="text-emerald-600 dark:text-emerald-400 font-medium"> You can optionally preview its tables on the right.</span>}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* ── Body ───────────────────────────────────────────────────────────── */}
                <div className="flex-1 flex overflow-hidden">

                    {/* Left Panel */}
                    <div className="w-[300px] flex flex-col border-r border-slate-200 dark:border-slate-800 shrink-0 bg-slate-50/30 dark:bg-slate-900/50">

                        {/* ── State A: No model chosen — show searchable list ── */}
                        {!selectedModel && (
                            <div id="dashboard-data-model-select" className="flex-1 flex flex-col min-h-0">
                                <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/50">
                                    <input
                                        type="text"
                                        placeholder="Search models..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="w-full px-3 py-2 text-[13px] border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400"
                                    />
                                </div>
                                <div className="flex-1 overflow-y-auto p-2">
                                    {loadingModels ? (
                                        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                                    ) : filteredModels.length === 0 ? (
                                        <div className="py-8 text-center text-[12px] text-slate-500 italic">No models found.</div>
                                    ) : filteredModels.map(m => (
                                        <button key={m.id} type="button" onClick={() => handleModelSelect(m)}
                                            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl mb-1 text-left hover:bg-blue-50 dark:hover:bg-blue-900/10 hover:border-blue-200 dark:hover:border-blue-800 border border-transparent transition-all group">
                                            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                                                <Database className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">{m.name}</div>
                                                <div className="text-[11px] text-slate-500 truncate">{m.description || m.storage_mode || 'Data Model'}</div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0 transition-colors" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── State B: Model chosen — show confirmation + optional preview list ── */}
                        {selectedModel && (
                            <div className="flex-1 flex flex-col min-h-0">

                                {/* ✅ Selected Model Confirmation Card */}
                                <div className="m-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                                    <div className="flex items-start gap-2.5">
                                        <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0 mt-0.5">
                                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-0.5">Model Selected</div>
                                            <div className="text-[13px] font-bold text-slate-900 dark:text-slate-100 truncate">{selectedModel.name}</div>
                                            {tables.length > 0 && (
                                                <div className="text-[11px] text-slate-500 mt-0.5">{tables.length} table{tables.length !== 1 ? 's' : ''} available</div>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => { setSelectedModel(null); setPreviewTable(null); setPreviewData([]); }}
                                        className="mt-2.5 w-full text-center text-[11px] font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-white dark:hover:bg-slate-800 py-1.5 rounded-lg transition-all border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                                    >
                                        Change model
                                    </button>
                                </div>

                                {/* Data Preview Section — clearly optional */}
                                {tables.length > 0 && (
                                    <div className="flex-1 flex flex-col min-h-0 border-t border-slate-200 dark:border-slate-800">
                                        {/* Collapsible section header */}
                                        <button
                                            type="button"
                                            onClick={() => setIsPreviewExpanded(v => !v)}
                                            className="flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors shrink-0"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Eye className="w-3.5 h-3.5 text-slate-400" />
                                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Data Preview</span>
                                                <span className="text-[10px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-full">Optional</span>
                                            </div>
                                            <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isPreviewExpanded ? 'rotate-90' : ''}`} />
                                        </button>

                                        {isPreviewExpanded && (
                                            <div className="flex-1 overflow-y-auto px-2 pb-2">
                                                <p className="px-2 pb-2 text-[11px] text-slate-400 leading-relaxed">
                                                    Click any table to peek at its data. This does not affect your selection.
                                                </p>
                                                {tables.map((t: any, i: number) => {
                                                    const tName = typeof t === 'string' ? t : t.name;
                                                    const isPreviewing = previewTable === tName;
                                                    return (
                                                        <button key={i} type="button" onClick={() => handleTablePreview(tName)}
                                                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg mb-0.5 text-left transition-all group ${isPreviewing
                                                                ? 'bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800'
                                                                : 'hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent'
                                                                }`}>
                                                            {/* Preview icon, not a "select" icon */}
                                                            <Eye className={`w-3.5 h-3.5 shrink-0 transition-colors ${isPreviewing ? 'text-violet-500' : 'text-slate-300 group-hover:text-slate-500'}`} />
                                                            <span className={`truncate flex-1 text-[12px] transition-colors ${isPreviewing ? 'text-violet-700 dark:text-violet-300 font-semibold' : 'text-slate-600 dark:text-slate-400'}`}>
                                                                {tName}
                                                            </span>
                                                            {isPreviewing && <span className="text-[9px] font-bold text-violet-500 uppercase tracking-wider shrink-0">Viewing</span>}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Right Panel ─────────────────────────────────────────────────── */}
                    <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-950">

                        {/* State: Previewing a table */}
                        {previewTable && (
                            <div className="flex-1 flex flex-col min-h-0">
                                <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0 bg-violet-50/40 dark:bg-violet-900/10">
                                    <div className="flex items-center gap-2">
                                        <Eye className="w-4 h-4 text-violet-400" />
                                        <span className="text-[13px] font-semibold text-slate-900 dark:text-slate-100">{previewTable}</span>
                                        <span className="text-[10px] font-bold text-violet-500 bg-violet-100 dark:bg-violet-900/30 px-2 py-0.5 rounded-full uppercase tracking-wider">Preview</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-slate-400">Top 50 rows</span>
                                        <button onClick={() => { setPreviewTable(null); setPreviewData([]); }}
                                            className="p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 transition-colors">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-auto p-4">
                                    {previewLoading ? (
                                        <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                                            <Loader2 className="w-7 h-7 animate-spin text-violet-500" />
                                            <div className="text-[12px]">Loading preview...</div>
                                        </div>
                                    ) : previewError ? (
                                        <div className="h-full flex items-center justify-center text-red-500 text-[13px] px-8 text-center">{previewError}</div>
                                    ) : previewData.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-slate-500 text-[13px]">No data found in this table.</div>
                                    ) : (
                                        <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden shadow-sm inline-block min-w-full">
                                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-sm border-collapse">
                                                <thead className="bg-slate-50 dark:bg-slate-900">
                                                    <tr>
                                                        {Object.keys(previewData[0] || {}).map(col => (
                                                            <th key={col} className="px-4 py-2.5 text-left font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest text-[10px] whitespace-nowrap bg-slate-50 dark:bg-slate-900">
                                                                {col}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40 bg-white dark:bg-slate-950">
                                                    {previewData.map((row, i) => (
                                                        <tr key={i} className="hover:bg-violet-50/30 dark:hover:bg-violet-900/10 transition-colors">
                                                            {Object.keys(row || {}).map(col => (
                                                                <td key={col} className="px-4 py-2 text-slate-600 dark:text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px] text-[12px]" title={String(row[col] ?? '')}>
                                                                    {String(row[col] ?? '')}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* State: Model selected, no preview yet — show model summary */}
                        {selectedModel && !previewTable && (
                            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-4 border border-emerald-200 dark:border-emerald-800">
                                    <Database className="w-8 h-8 text-emerald-500 dark:text-emerald-400" />
                                </div>
                                <div className="text-[16px] font-bold text-slate-900 dark:text-slate-100 mb-1">{selectedModel.name}</div>
                                {selectedModel.description && (
                                    <p className="text-[12px] text-slate-500 mb-4 max-w-[320px] leading-relaxed">{selectedModel.description}</p>
                                )}
                                <div className="flex items-center gap-4 mb-6">
                                    {tables.length > 0 && (
                                        <div className="flex items-center gap-1.5 text-[12px] text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full">
                                            <Layers className="w-3.5 h-3.5" />
                                            {tables.length} table{tables.length !== 1 ? 's' : ''}
                                        </div>
                                    )}
                                    {selectedModel.storage_mode && (
                                        <div className="text-[12px] text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full capitalize">
                                            {selectedModel.storage_mode}
                                        </div>
                                    )}
                                </div>

                                {/* Nudge: highlight the confirm action */}
                                <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 max-w-[280px]">
                                    <div className="text-[12px] font-semibold text-blue-700 dark:text-blue-300">Ready to apply this model?</div>
                                    <div className="text-[11px] text-blue-600/70 dark:text-blue-400/70 leading-relaxed">
                                        Click <strong>Confirm Selection</strong> below, or optionally peek at tables using the left panel.
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-blue-400 rotate-90 mt-1 animate-bounce" />
                                </div>

                                {tables.length > 0 && (
                                    <p className="mt-4 text-[11px] text-slate-400">
                                        <Eye className="w-3 h-3 inline mr-1 opacity-70" />
                                        Use the left panel to optionally preview table data.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* State: No model selected yet */}
                        {!selectedModel && (
                            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                                    <Database className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                                </div>
                                <div className="text-[14px] font-semibold text-slate-600 dark:text-slate-400 mb-1">No Data Model Selected</div>
                                <div className="text-[12px] text-slate-400 max-w-[240px] leading-relaxed">
                                    Pick a model from the left to get started. You can preview its tables before confirming.
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Footer ─────────────────────────────────────────────────────────── */}
                <div className="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between shrink-0">
                    <div className="text-[12px] text-slate-500">
                        {selectedModel
                            ? <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /><span className="text-slate-600 dark:text-slate-300 font-medium">{selectedModel.name}</span> <span className="text-slate-400">selected</span></span>
                            : <span className="text-slate-400">No model selected</span>
                        }
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-[12px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors">
                            Cancel
                        </button>
                        <button
                            id="dashboard-data-model-confirm"
                            onClick={() => selectedModel && onConfirm(selectedModel)}
                            disabled={!selectedModel}
                            className={`px-5 py-2 text-[12px] font-bold text-white rounded-lg shadow-sm transition-all flex items-center gap-2 ${selectedModel
                                ? 'bg-blue-600 hover:bg-blue-700 ring-2 ring-blue-200 dark:ring-blue-900 hover:ring-blue-300'
                                : 'bg-blue-300 dark:bg-blue-800 opacity-50 cursor-not-allowed'
                                }`}
                        >
                            <Check className="w-4 h-4" />
                            Confirm Selection
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
    return createPortal(modalContent, document.body);
};
